import axios from 'axios';
import isBoolean from 'lodash/isBoolean';
import isNil from 'lodash/isNil';

import { RESET_ALL as RESET_ON_LOGOUT } from 'redux-store/reducers/common-reducer';

import { createReducer } from 'utils/redux';
import { API, get } from 'utils/api';
import { logError } from 'utils/log';
import { TAB_LABELS, convertItemToCheckbox } from 'modules/insights/sov/utils/queries/sov-queries';
import {
  getEndpointConfig
} from 'modules/insights/sov/utils/consumer-insights/sov-consumer-insights';
import {
  getDefaultResultsMap,
  getPreparedCustomKeywords,
  getPreparedIncludedKeywords
} from 'modules/insights/sov/utils/queries/search-results/sov-queries-search-results';

import type {
  Action,
  CheckboxesListItem,
  Dispatch,
  GetState,
  ThunkAction
} from 'types';
import type {
  SOVQuerySearchResultsCheckboxes,
  SOVQuerySearchResultsState,
  SOVQuerySearchResultsType,
  SOVQueryStepType
} from 'modules/insights/sov/types';

const { CI } = API.END_POINTS;

const RESULTS_PROCESSING = 'sov-ci/QUERY_SEARCH_RESULTS_PROCESSING';
const RESULTS_LOADED = 'sov-ci/QUERY_SEARCH_RESULTS_LOADED';
const RESULTS_UPDATED = 'sov-ci/QUERY_SEARCH_RESULTS_UPDATED';
const RESULTS_RESET = 'sov-ci/QUERY_SEARCH_RESULTS_RESET';

let fetchCreatorsCancelToken;

const initialState = {
  results: getDefaultResultsMap(),
  resultsCustom: getDefaultResultsMap(),
  isLoading: false
};

const setStatus = (status: boolean): Action => ({
  type: RESULTS_PROCESSING,

  payload: {
    status
  }
});

const resultsLoaded = (
  results: Partial<SOVQuerySearchResultsCheckboxes>,
  resultsType: SOVQuerySearchResultsType,
  stepType: SOVQueryStepType
): Action => ({
  type: RESULTS_LOADED,

  payload: {
    results,
    resultsType,
    stepType
  }
});

const itemsUpdated = (
  results: Array<CheckboxesListItem>,
  resultsType: SOVQuerySearchResultsType,
  activeTab: string,
  stepType: SOVQueryStepType
): Action => ({
  type: RESULTS_UPDATED,

  payload: {
    results,
    resultsType,
    activeTab,
    stepType
  }
});

export const resetSearchResults = (): Action => ({
  type: RESULTS_RESET
});

export const fetchSearchResults = (
  reportId: number,
  searchTerm: string,
  queryId: number | undefined | null,
  stepType: SOVQueryStepType
): ThunkAction => async (dispatch: Dispatch, getState: GetState) => {
  dispatch(setStatus(true));

  if (fetchCreatorsCancelToken) {
    fetchCreatorsCancelToken.cancel();
  }

  fetchCreatorsCancelToken = axios.CancelToken.source();

  try {
    const url = CI.SOV.getKeywords(searchTerm, queryId);
    const configOptions = getEndpointConfig(reportId);

    const updatedConfigOptions = {
      ...configOptions,
      config: {
        ...configOptions.config,
        cancelToken: fetchCreatorsCancelToken.token
      }
    };

    const response = await get(url, updatedConfigOptions.config, updatedConfigOptions.options);
    const results = response.data || {};
    const mergedQueries = getState().sovQueries.queriesMerged;
    const convertedResults = {};
    const convertedResultsCustom = {};
    let preparedResults = results;
    let preparedResultsCustom = {};

    if (queryId) {
      preparedResults = getPreparedIncludedKeywords(queryId, stepType, results, mergedQueries);
      preparedResultsCustom = getPreparedCustomKeywords(queryId, stepType, mergedQueries);
    }

    // convert to checkboxes
    TAB_LABELS.forEach(key => {
      convertedResults[key] = (preparedResults[key] || results).map(convertItemToCheckbox);
      convertedResultsCustom[key] = (preparedResultsCustom[key] || []).map(convertItemToCheckbox);
    });

    dispatch(setStatus(false));

    dispatch(resultsLoaded(convertedResultsCustom, 'resultsCustom', stepType));
    return dispatch(resultsLoaded(convertedResults, 'results', stepType));
  } catch (error) {
    logError(error);
    throw error;
  } finally {
    dispatch(setStatus(false));
  }
};

export const updateSearchResults = (
  item: CheckboxesListItem,
  resultsType: SOVQuerySearchResultsType,
  activeTab: string,
  stepType: SOVQueryStepType,
  statusOverride?: boolean | null
): ThunkAction => (dispatch: Dispatch, getState: GetState) => {
  const activeResults = getState().sovQueriesSearchResultsConsumerInsights[resultsType][stepType][activeTab];
  const updatedResults = (activeResults || []).map(result => {
    let status = result.isSelected;

    if (isNil(statusOverride) && result.id === item.id) {
      status = !result.isSelected;
    }

    return {
      ...result,
      isSelected: isBoolean(statusOverride) ? statusOverride : status
    };
  });

  return dispatch(itemsUpdated(updatedResults, resultsType, activeTab, stepType));
};

export const addSearchResultCustom = (item: CheckboxesListItem, activeTab: string, stepType: SOVQueryStepType): ThunkAction => (dispatch: Dispatch, getState: GetState) => {
  const activeResults = getState().sovQueriesSearchResultsConsumerInsights.resultsCustom[stepType][activeTab];
  const foundItem = activeResults.find(result => result.label === item.label);
  let updatedResults = [...activeResults];

  // prevent duplicates
  if (isNil(foundItem)) {
    updatedResults = activeResults.concat([item]);
  }

  return dispatch(itemsUpdated(updatedResults, 'resultsCustom', activeTab, stepType));
};

export const removeSearchResultCustom = (item: CheckboxesListItem, activeTab: string, stepType: SOVQueryStepType): ThunkAction => (dispatch: Dispatch, getState: GetState) => {
  const activeResults = getState().sovQueriesSearchResultsConsumerInsights.resultsCustom[stepType][activeTab];
  const updatedResults = activeResults.filter(result => result.id !== item.id);

  return dispatch(itemsUpdated(updatedResults, 'resultsCustom', activeTab, stepType));
};

const handleProcessing = (state: SOVQuerySearchResultsState, { status }): SOVQuerySearchResultsState => ({
  ...state,
  isLoading: status
});

const handleLoaded = (state: SOVQuerySearchResultsState, { results, resultsType, stepType }): SOVQuerySearchResultsState => ({
  ...state,

  [resultsType]: {
    ...state[resultsType],
    [stepType]: results
  },

  isLoading: false
});

const handleUpdated = (
  state: SOVQuerySearchResultsState,
  { results, resultsType, activeTab, stepType }
): SOVQuerySearchResultsState => ({
  ...state,

  [resultsType]: {
    ...state[resultsType],
    [String(stepType)]: {
      ...state[resultsType][stepType],
      [activeTab]: results
    }
  }
});

export default createReducer<SOVQuerySearchResultsState, any>('sovQueriesSearchResultsConsumerInsights', initialState, {
  [RESET_ON_LOGOUT]: () => initialState,
  [RESULTS_RESET]: () => initialState,
  [RESULTS_PROCESSING]: handleProcessing,
  [RESULTS_LOADED]: handleLoaded,
  [RESULTS_UPDATED]: handleUpdated
});
